import React, { useState } from 'react';
import { RefreshCw, ExternalLink, Copy, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { toast } from 'sonner';

export const PLATFORM_SETTINGS_CARD =
  'bg-zinc-900/50 border-zinc-800 text-white';
export const PLATFORM_SETTINGS_INPUT =
  'bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500';
export const PLATFORM_PRIMARY_BUTTON =
  'bg-emerald-600 hover:bg-emerald-700 text-white';

export const DEFAULT_DOMAIN_STATUS = {
  spf: 'not_checked',
  dkim: 'not_checked',
  tracking_cname: 'not_checked',
  dmarc: 'not_checked',
  verified: false,
  last_checked_at: null,
};

export const DOMAIN_ASSIGNMENT_TYPES = [
  { id: 'calendar', label: 'Calendar Domain' },
  { id: 'payments', label: 'Payments' },
  { id: 'one_on_one', label: 'One-on-One Conversation Domain' },
  { id: 'bulk', label: 'Bulk Email Domain' },
  { id: 'campaign', label: 'Campaign Domain' },
  { id: 'workflow', label: 'Workflow Domain' },
  { id: 'client_portal', label: 'Client Portal Domain' },
  { id: 'default', label: 'Default Dedicated Domain' },
];

export const getDomainFromEmail = (email) => {
  if (!email || !email.includes('@')) return '';
  return email.split('@')[1].trim();
};

export const deriveTrackingDomain = (mailgunDomain) => {
  if (!mailgunDomain) return '';
  return `email.${mailgunDomain.replace(/^mg\./, '')}`;
};

export const deriveReplyDomain = (replyToEmail, mailgunDomain) => {
  const fromReply = getDomainFromEmail(replyToEmail);
  if (fromReply) return fromReply;
  if (!mailgunDomain) return '';
  return mailgunDomain.replace(/^mg\./, '');
};

const DOMAIN_PATTERN = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/;

export const validateDomainInput = (value) => {
  const domain = (value || '').trim().toLowerCase();
  if (!domain) {
    return { valid: false, error: 'Domain is required.', domain: '' };
  }
  if (domain !== value.trim()) {
    return { valid: false, error: 'Use only lowercase letters in the domain name.', domain };
  }
  if (!DOMAIN_PATTERN.test(domain)) {
    return { valid: false, error: 'Enter a valid domain or subdomain (e.g. mg.fundelite.com).', domain };
  }
  return { valid: true, error: null, domain };
};

export const DNS_SOURCE_LABELS = {
  provider: 'Provider-provided',
  generated: 'Generated fallback',
  pending_provider: 'Pending provider value',
  recommended: 'Recommended',
};

export const DKIM_PENDING_MESSAGE =
  'Pending from Mailgun. Add the domain in Mailgun or click Check Verification to fetch DNS records.';

export const buildFullDnsName = (host, sendingDomain) => {
  const domain = (sendingDomain || '').trim().toLowerCase();
  if (!domain || !host) return '';

  const parts = domain.split('.');
  const rootDomain = parts.length > 2 ? parts.slice(1).join('.') : domain;

  if (host === '@') return domain;
  if (host === '_dmarc') return `_dmarc.${rootDomain}`;
  if (host.includes('.') && (host === domain || host.endsWith(`.${rootDomain}`))) {
    return host;
  }
  return `${host}.${rootDomain}`;
};

const findProviderRecord = (providerRecords, type) => {
  if (!Array.isArray(providerRecords)) return null;
  const needle = String(type).toUpperCase();
  return providerRecords.find((row) => {
    const recordType = String(row?.record_type || row?.type || row?.label || '').toUpperCase();
    const purpose = String(row?.purpose || row?.id || '').toUpperCase();
    return (
      recordType === needle ||
      purpose === needle ||
      purpose.includes(needle) ||
      String(row?.id || '').toLowerCase() === needle.toLowerCase()
    );
  });
};

export const truncateDnsDisplay = (value, maxLen = 52) => {
  if (!value) return '';
  if (value.length <= maxLen) return value;
  return `${value.slice(0, maxLen)}…`;
};

export const generateMailgunDnsRecords = (sendingDomain, options = {}) => {
  const domain = (sendingDomain || '').trim().toLowerCase();
  if (!domain) return [];

  const { providerRecords = [] } = options;
  const parts = domain.split('.');
  const hasSubdomain = parts.length > 2;
  const hostPrefix = hasSubdomain ? parts[0] : '@';
  const rootDomain = hasSubdomain ? parts.slice(1).join('.') : domain;
  const trackingHost = hasSubdomain ? `email.${parts[0]}` : `email.${domain}`;
  const dkimHost = hasSubdomain ? `k1._domainkey.${parts[0]}` : `k1._domainkey.${domain}`;

  const providerDkim = findProviderRecord(providerRecords, 'DKIM');
  const providerDkimValue =
    providerDkim?.value || providerDkim?.expected_value || providerDkim?.actual_value || null;
  const providerTracking = findProviderRecord(providerRecords, 'CNAME');
  const providerTrackingValue =
    providerTracking?.expected_value || providerTracking?.value || null;
  const providerSpf = findProviderRecord(providerRecords, 'SPF');
  const providerSpfValue = providerSpf?.expected_value || providerSpf?.value || null;

  const spfValue = providerSpfValue || 'v=spf1 include:mailgun.org ~all';
  const trackingValue = providerTrackingValue || 'mailgun.org';

  const dkimRecord = {
    id: 'dkim',
    record: 'TXT',
    label: 'DKIM',
    purpose: 'DKIM',
    host: dkimHost,
    fullDnsName: buildFullDnsName(dkimHost, domain),
    value: providerDkimValue,
    copyValue: providerDkimValue,
    valuePending: !providerDkimValue,
    pendingMessage: DKIM_PENDING_MESSAGE,
    source: providerDkimValue ? 'provider' : 'pending_provider',
    status: providerDkim?.status || 'not_checked',
  };

  return [
    {
      id: 'spf',
      record: 'TXT',
      label: 'SPF',
      purpose: 'SPF',
      host: hostPrefix,
      fullDnsName: buildFullDnsName(hostPrefix, domain),
      value: spfValue,
      copyValue: spfValue,
      valuePending: false,
      source: providerSpfValue ? 'provider' : 'generated',
      status: 'not_checked',
    },
    dkimRecord,
    {
      id: 'tracking',
      record: 'CNAME',
      label: 'Tracking CNAME',
      purpose: 'Tracking CNAME',
      host: trackingHost,
      fullDnsName: buildFullDnsName(trackingHost, domain),
      value: trackingValue,
      copyValue: trackingValue,
      valuePending: false,
      source: providerTrackingValue ? 'provider' : 'generated',
      status: 'not_checked',
    },
    {
      id: 'mxa',
      record: 'MX',
      label: 'MX (primary)',
      purpose: 'Mailgun receiving/bounce handling',
      host: hostPrefix,
      fullDnsName: buildFullDnsName(hostPrefix, domain),
      value: 'mxa.mailgun.org',
      copyValue: 'mxa.mailgun.org',
      priority: '10',
      valuePending: false,
      source: 'generated',
      status: 'not_checked',
    },
    {
      id: 'mxb',
      record: 'MX',
      label: 'MX (secondary)',
      purpose: 'Mailgun receiving/bounce handling',
      host: hostPrefix,
      fullDnsName: buildFullDnsName(hostPrefix, domain),
      value: 'mxb.mailgun.org',
      copyValue: 'mxb.mailgun.org',
      priority: '10',
      valuePending: false,
      source: 'generated',
      status: 'not_checked',
    },
    {
      id: 'dmarc',
      record: 'TXT',
      label: 'DMARC',
      purpose: 'DMARC',
      host: '_dmarc',
      fullDnsName: buildFullDnsName('_dmarc', domain),
      value: `v=DMARC1; p=none; rua=mailto:dmarc@${rootDomain}`,
      copyValue: `v=DMARC1; p=none; rua=mailto:dmarc@${rootDomain}`,
      valuePending: false,
      source: 'recommended',
      status: 'recommended',
    },
  ];
};

export const REQUIRED_DNS_VERIFICATION_KEYS = ['spf', 'dkim', 'tracking_cname', 'mxa', 'mxb'];

export const evaluateVerificationResponse = (apiResponse) => {
  const status = apiResponse?.verification_status || {};
  const message = apiResponse?.message || '';
  const isPlaceholder =
    status.verified !== true &&
    (message.toLowerCase().includes('not yet connected') ||
      message.toLowerCase().includes('placeholder') ||
      message.toLowerCase().includes('manually in mailgun'));

  const recordStatuses = {
    spf: status.spf || 'not_checked',
    dkim: status.dkim || 'not_checked',
    tracking_cname: status.tracking_cname || 'not_checked',
    mxa: status.mxa || status.mx || 'not_checked',
    mxb: status.mxb || status.mx || 'not_checked',
    dmarc: status.dmarc || 'not_checked',
  };

  const coreVerified = ['spf', 'dkim', 'tracking_cname'].every(
    (key) => recordStatuses[key] === 'verified'
  );
  const mxVerified =
    recordStatuses.mxa === 'verified' && recordStatuses.mxb === 'verified';
  const allVerified = Boolean(status.verified) && coreVerified && mxVerified;

  const hasFailed = Object.values(recordStatuses).some(
    (value) => value === 'failed' || value === 'missing'
  );
  const hasPending = Object.entries(recordStatuses).some(
    ([key, value]) =>
      REQUIRED_DNS_VERIFICATION_KEYS.includes(key) &&
      (value === 'pending' || value === 'not_checked' || value === 'recommended')
  );

  if (allVerified) {
    return {
      level: 'success',
      allVerified: true,
      recordStatuses,
      isPlaceholder: false,
      message: 'All required DNS records are verified.',
      toastMessage: 'All required DNS records are verified.',
    };
  }

  if (hasFailed) {
    return {
      level: 'error',
      allVerified: false,
      recordStatuses,
      isPlaceholder,
      message: 'Some DNS records are not verified yet.',
      toastMessage: 'Verification failed. Review the DNS records and try again.',
    };
  }

  if (isPlaceholder || hasPending) {
    const partialVerified = ['spf', 'dkim', 'tracking_cname', 'mxa', 'mxb'].some(
      (key) => recordStatuses[key] === 'verified'
    );
    return {
      level: isPlaceholder ? 'info' : partialVerified ? 'warning' : 'info',
      allVerified: false,
      recordStatuses,
      isPlaceholder,
      message: isPlaceholder
        ? 'DNS verification is pending provider confirmation.'
        : partialVerified
          ? 'Verification checked. Some records are still pending.'
          : 'Verification is not confirmed yet.',
      toastMessage: isPlaceholder
        ? 'Verification check submitted. DNS records are still pending.'
        : partialVerified
          ? 'Verification checked. Some records are still pending.'
          : 'Verification is not confirmed yet.',
    };
  }

  return {
    level: 'warning',
    allVerified: false,
    recordStatuses,
    isPlaceholder: false,
    message: 'Verification is not confirmed yet.',
    toastMessage: 'Verification checked. Some records are still pending.',
  };
};

export const DnsSourceBadge = ({ source }) => {
  const styles = {
    provider: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    generated: 'bg-zinc-700/60 text-zinc-300 border-zinc-600',
    pending_provider: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
    recommended: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  };
  const label = DNS_SOURCE_LABELS[source] || DNS_SOURCE_LABELS.generated;

  return (
    <Badge className={`${styles[source] || styles.generated} border text-[10px] font-medium`}>
      {label}
    </Badge>
  );
};

export const DnsCopyButton = ({ value, title = 'Copy value', disabled = false }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (disabled || !value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success('Copied full value.');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={handleCopy}
      disabled={disabled || !value}
      className="h-8 w-8 p-0 shrink-0 border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 hover:text-emerald-300 disabled:opacity-40"
      title={title}
      aria-label={title}
      data-testid={`copy-dns-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
    </Button>
  );
};

const DnsValueCell = ({ record }) => {
  const [expanded, setExpanded] = useState(false);

  if (record.valuePending) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-amber-300/90 leading-relaxed">{record.pendingMessage}</p>
        <DnsCopyButton
          value={null}
          title="DKIM value pending from Mailgun"
          disabled
        />
      </div>
    );
  }

  const rawValue = record.copyValue || record.value || '';
  const displayValue = expanded ? rawValue : truncateDnsDisplay(rawValue);
  const isLong = rawValue.length > 52;

  if (record.priority) {
    return (
      <div className="flex items-start gap-2 min-w-0">
        <div className="flex-1 min-w-0 flex flex-wrap items-center gap-2">
          <span className="font-mono text-xs text-zinc-200 truncate" title={rawValue}>
            {rawValue}
          </span>
          <Badge className="bg-zinc-800 text-zinc-400 border-zinc-700 text-[10px]">
            Priority {record.priority}
          </Badge>
        </div>
        <DnsCopyButton value={rawValue} title="Copy full value" />
      </div>
    );
  }

  return (
    <div className="flex items-start gap-2 min-w-0">
      <div
        className={`flex-1 min-w-0 font-mono text-xs text-amber-100/90 bg-zinc-950/60 border border-zinc-700 rounded-md px-3 py-2 ${
          expanded ? 'whitespace-pre-wrap break-all' : 'truncate'
        }`}
        title={rawValue}
      >
        {displayValue}
      </div>
      <div className="flex flex-col gap-1 shrink-0">
        <DnsCopyButton value={rawValue} title="Copy full value" />
        {isLong && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setExpanded((v) => !v)}
            className="h-8 w-8 p-0 text-zinc-400 hover:text-amber-400"
            title={expanded ? 'Collapse value' : 'Expand value'}
          >
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </Button>
        )}
      </div>
    </div>
  );
};

const DnsHostCell = ({ host, fullDnsName }) => (
  <div className="space-y-2 min-w-0">
    <div className="flex items-center gap-2 min-w-0">
      <code
        className="flex-1 min-w-0 font-mono text-xs text-zinc-200 bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 truncate"
        title={host}
      >
        {host}
      </code>
      <DnsCopyButton value={host} title="Copy host" />
    </div>
    <div className="flex items-center gap-2 min-w-0">
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1">Full DNS Name</p>
        <code
          className="block font-mono text-xs text-amber-400/90 bg-zinc-950/40 border border-zinc-800 rounded-md px-3 py-2 truncate"
          title={fullDnsName}
        >
          {fullDnsName}
        </code>
      </div>
      <DnsCopyButton value={fullDnsName} title="Copy full DNS name" />
    </div>
  </div>
);

export const DnsRecordsTable = ({ records = [], domainStatus, verificationStatuses }) => {
  const statusMap = verificationStatuses || {
    spf: domainStatus?.spf,
    dkim: domainStatus?.dkim,
    tracking: domainStatus?.tracking_cname,
    tracking_cname: domainStatus?.tracking_cname,
    mxa: domainStatus?.mxa || domainStatus?.mx,
    mxb: domainStatus?.mxb || domainStatus?.mx,
    dmarc: domainStatus?.dmarc,
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2 text-xs text-zinc-400 leading-relaxed">
        <p>
          If your DNS provider manages the root domain, enter the short Host value. If your DNS
          provider asks for the full DNS name, use the Full DNS Name. Do not add both.
        </p>
        <p>
          Long DNS values may be shortened visually. The copy button always copies the full value.
        </p>
      </div>

      <div
        className="rounded-xl border border-zinc-800 overflow-hidden bg-zinc-900/80"
        data-testid="dns-records-table"
      >
        <div className="hidden lg:grid grid-cols-12 gap-3 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-zinc-500 border-b border-zinc-800 bg-zinc-950/40">
          <div className="col-span-2">Record</div>
          <div className="col-span-3">Host / Full DNS Name</div>
          <div className="col-span-4">Required Value</div>
          <div className="col-span-2">Source</div>
          <div className="col-span-1 text-right">Status</div>
        </div>
        <div className="divide-y divide-zinc-800">
          {records.map((record) => {
            const status = record.status || statusMap[record.id] || 'not_checked';
            return (
              <div
                key={record.id}
                className="grid grid-cols-1 lg:grid-cols-12 gap-4 px-4 py-4 items-start"
              >
                <div className="lg:col-span-2 space-y-1">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30 font-mono text-xs">
                      {record.record}
                    </Badge>
                    <span className="text-xs text-zinc-400 lg:hidden">{record.label}</span>
                  </div>
                  <p className="text-xs text-zinc-500 hidden lg:block">{record.label}</p>
                </div>
                <div className="lg:col-span-3">
                  <DnsHostCell host={record.host} fullDnsName={record.fullDnsName} />
                </div>
                <div className="lg:col-span-4">
                  <DnsValueCell record={record} />
                </div>
                <div className="lg:col-span-2">
                  <DnsSourceBadge source={record.source} />
                </div>
                <div className="lg:col-span-1 flex lg:justify-end">
                  <DnsStatusBadge status={status} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export const DnsStatusBadge = ({ status }) => {
  const statusConfig = {
    verified: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Verified' },
    pending: { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Pending' },
    missing: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Missing' },
    failed: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Failed' },
    not_checked: { bg: 'bg-zinc-600/50', text: 'text-zinc-400', label: 'Not Checked' },
    recommended: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Recommended' },
  };

  const config = statusConfig[status] || statusConfig.not_checked;

  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

export const OverallVerificationBadge = ({ verified, domainStatus }) => {
  const recordStatuses = {
    spf: domainStatus?.spf,
    dkim: domainStatus?.dkim,
    tracking_cname: domainStatus?.tracking_cname,
    mxa: domainStatus?.mxa || domainStatus?.mx,
    mxb: domainStatus?.mxb || domainStatus?.mx,
  };

  const allVerified =
    Boolean(verified) &&
    ['spf', 'dkim', 'tracking_cname', 'mxa', 'mxb'].every(
      (key) => recordStatuses[key] === 'verified'
    );

  if (allVerified) {
    return (
      <Badge className="bg-emerald-500/20 text-emerald-400 border-0">Verified</Badge>
    );
  }

  const hasPending =
    Object.entries(recordStatuses).some(
      ([, value]) => value === 'pending' || value === 'not_checked'
    ) || domainStatus?.dkim === 'pending';

  if (hasPending) {
    return (
      <Badge className="bg-amber-500/20 text-amber-400 border-0">Pending</Badge>
    );
  }

  return (
    <Badge className="bg-zinc-600/50 text-zinc-400 border-0">Not Verified</Badge>
  );
};

const DNS_RECORD_ROWS = [
  { key: 'spf', label: 'SPF' },
  { key: 'dkim', label: 'DKIM' },
  { key: 'tracking_cname', label: 'Tracking CNAME' },
  { key: 'dmarc', label: 'DMARC' },
];

export const DomainVerificationPanel = ({
  domainStatus,
  onCheckDomain,
  checking = false,
  showMailgunLink = true,
  manageDomainsPath,
  onManageDomains,
}) => (
  <div className="space-y-3">
    <div className="space-y-2">
      {DNS_RECORD_ROWS.map((item) => (
        <div
          key={item.key}
          className="flex items-center justify-between py-2 border-b border-zinc-800 last:border-0"
        >
          <span className="text-sm text-zinc-300">{item.label}</span>
          <DnsStatusBadge status={domainStatus?.[item.key] || 'not_checked'} />
        </div>
      ))}
    </div>

    <p className="text-xs text-zinc-500">
      DNS records can be added manually in Mailgun dashboard or verified here when connected.
    </p>

    <div className="flex flex-col gap-2">
      {onCheckDomain && (
        <Button
          variant="outline"
          size="sm"
          className="w-full border-zinc-700 text-zinc-300"
          onClick={onCheckDomain}
          disabled={checking}
          data-testid="check-domain-verification-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${checking ? 'animate-spin' : ''}`} />
          Check Verification
        </Button>
      )}
      {onManageDomains && manageDomainsPath && (
        <Button
          variant="outline"
          size="sm"
          className="w-full border-zinc-700 text-zinc-300"
          onClick={onManageDomains}
          data-testid="manage-domains-btn"
        >
          Manage Domains
        </Button>
      )}
      {showMailgunLink && (
        <Button
          variant="outline"
          size="sm"
          className="w-full border-zinc-700 text-zinc-300"
          onClick={() => window.open('https://app.mailgun.com/app/domains', '_blank')}
        >
          <ExternalLink className="w-4 h-4 mr-2" />
          Manage in Mailgun
        </Button>
      )}
    </div>
  </div>
);
